import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * Cart drawer behavior plus Jélie's free-shipping progress.
 * The modal lookup deliberately avoids using `closest('dialog:modal')` directly,
 * because older Safari versions can throw on the :modal pseudo-class.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** Free Mondial Relay shipping from €69 (amount in cents). */
  #freeShippingThreshold = 6900;

  /** @type {MutationObserver | null} */
  #cartSummaryObserver = null;

  /** @type {number | undefined} */
  #freeShippingUpdateTimer = undefined;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#ensureFreeShippingStyles();
    this.#observeCartSummary();
    this.#updateFreeShippingProgress();

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#cartSummaryObserver?.disconnect();
    this.#cartSummaryObserver = null;
    clearTimeout(this.#freeShippingUpdateTimer);
    this.#freeShippingUpdateTimer = undefined;
  }

  #observeCartSummary() {
    if (this.#cartSummaryObserver) return;

    this.#cartSummaryObserver = new MutationObserver((mutations) => {
      const summaryChanged = mutations.some((mutation) => {
        const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;

        if (target?.closest('.jelie-free-shipping')) return false;
        if (target?.closest('.cart-drawer__summary')) return true;

        return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => {
          if (!(node instanceof Element)) return false;
          return node.matches('.cart-drawer__summary') || Boolean(node.querySelector('.cart-drawer__summary'));
        });
      });

      if (!summaryChanged) return;

      clearTimeout(this.#freeShippingUpdateTimer);
      this.#freeShippingUpdateTimer = window.setTimeout(() => {
        this.#freeShippingUpdateTimer = undefined;
        this.#updateFreeShippingProgress();
      }, 80);
    });

    this.#cartSummaryObserver.observe(this, { childList: true, subtree: true });
  }

  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#updateFreeShippingProgress();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * Safari-safe modal lookup. Older Safari may reject the `:modal` selector,
   * so first find the closest dialog and then test :modal behind try/catch.
   * If :modal is unsupported, an open dialog is the safe fallback here.
   *
   * @param {EventTarget | null} target
   * @returns {HTMLDialogElement | null}
   */
  #findSourceModal(target) {
    if (!(target instanceof Element)) return null;

    const dialog = target.closest('dialog');
    if (!(dialog instanceof HTMLDialogElement)) return null;

    try {
      return dialog.matches(':modal') ? dialog : null;
    } catch {
      return dialog.open ? dialog : null;
    }
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;
    const sourceModal = this.#findSourceModal(event.target);

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => {
            this.#updateStickyState();
            this.#updateFreeShippingProgress();
          });

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #ensureFreeShippingStyles() {
    if (document.getElementById('jelie-free-shipping-styles')) return;

    const style = document.createElement('style');
    style.id = 'jelie-free-shipping-styles';
    style.textContent = `
      .jelie-free-shipping {
        display: block;
        width: calc(100% - 24px);
        max-width: 420px;
        margin-left: auto;
        margin-right: auto;
        box-sizing: border-box;
        align-self: center;
        text-align: center;
      }
      .jelie-free-shipping__message {
        width: 100%;
        margin: 0 0 8px;
        text-align: center;
        font-size: var(--font-size--sm);
        line-height: 1.35;
      }
      .jelie-free-shipping__track {
        display: block;
        width: 100%;
        height: 8px;
        margin-left: auto;
        margin-right: auto;
        box-sizing: border-box;
        overflow: hidden;
        border-radius: 999px;
        background: rgb(var(--color-foreground-rgb) / 0.12);
      }
      .jelie-free-shipping__fill {
        display: block;
        height: 100%;
        border-radius: inherit;
        background: var(--color-foreground);
        transition: width 300ms ease;
      }
      .jelie-free-shipping__labels {
        display: flex;
        width: 100%;
        justify-content: space-between;
        margin: 4px auto 0;
        color: var(--color-foreground-subdued);
        font-size: var(--font-size--2xs);
      }
      @media screen and (max-width: 749px) {
        .jelie-free-shipping {
          width: calc(100% - 16px);
          max-width: none;
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .jelie-free-shipping__fill { transition: none; }
      }
    `;
    document.head.append(style);
  }

  async #updateFreeShippingProgress() {
    const summary = this.querySelector('.cart-drawer__summary');
    if (!summary) return;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) return;

      const cart = await response.json();
      if (!cart.item_count) {
        summary.querySelector('.jelie-free-shipping')?.remove();
        return;
      }

      const remaining = Math.max(this.#freeShippingThreshold - cart.total_price, 0);
      const progress = Math.min(Math.max((cart.total_price / this.#freeShippingThreshold) * 100, 0), 100);
      const formatter = new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
        style: 'currency',
        currency: cart.currency || 'EUR',
      });

      let progressElement = summary.querySelector('.jelie-free-shipping');
      if (!progressElement) {
        progressElement = document.createElement('div');
        progressElement.className = 'jelie-free-shipping';
        progressElement.setAttribute('role', 'status');
        progressElement.setAttribute('aria-live', 'polite');
        summary.prepend(progressElement);
      }

      const message =
        remaining > 0
          ? `Plus que <strong>${formatter.format(remaining / 100)}</strong> pour profiter de la livraison offerte`
          : '🎉 Livraison offerte !';

      progressElement.innerHTML = `
        <p class="jelie-free-shipping__message">${message}</p>
        <div class="jelie-free-shipping__track" aria-hidden="true">
          <span class="jelie-free-shipping__fill" style="width: ${progress.toFixed(2)}%"></span>
        </div>
        <div class="jelie-free-shipping__labels" aria-hidden="true">
          <span>0 €</span>
          <span>69 €</span>
        </div>
      `;

      requestAnimationFrame(() => this.#updateStickyState());
    } catch (error) {
      console.warn('[cart-drawer] Free-shipping progress unavailable:', error);
    }
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
